import { Box, Checkbox, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useIntl } from 'react-intl';

import { InfoIconTooltip, TextField, Tooltip } from '@verantos-dev/shared-components';

export type BaselinePeriodRestrictionProps = {
  viewMode: boolean;
  isBaselinePeriodRestrictionEnabled: boolean;
  setIsBaselinePeriodRestrictionEnabled: (value: boolean) => void;
  baselinePeriodDaysBeforeIndexDate: number | undefined;
  setBaselinePeriodDaysBeforeIndexDate: (value: number | undefined) => void;
  hasIndexDate: boolean;
};

export const BaselinePeriodRestriction = ({
  viewMode,
  baselinePeriodDaysBeforeIndexDate,
  isBaselinePeriodRestrictionEnabled,
  setIsBaselinePeriodRestrictionEnabled,
  setBaselinePeriodDaysBeforeIndexDate,
  hasIndexDate,
}: BaselinePeriodRestrictionProps) => {
  const intl = useIntl();
  const theme = useTheme();

  const onClickBaselinePeriodRestrictionCheckbox = () => {
    !viewMode &&
      hasIndexDate &&
      setIsBaselinePeriodRestrictionEnabled(!isBaselinePeriodRestrictionEnabled);
  };

  const onDaysBeforeIndexDateChange = (_id: string, value: string) => {
    const isNumberOrEmpty = value === '' || /^\d+$/.test(value);
    isNumberOrEmpty &&
      setBaselinePeriodDaysBeforeIndexDate(value === '' ? undefined : parseInt(value));
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center' }}>
        <Typography sx={{ fontWeight: 500, color: theme.palette.text.primary }} variant="body1">
          {intl.formatMessage({
            id: 'projects.cohortBuilderPage.studyPeriodDialogBaselinePeriod.title',
          })}
        </Typography>
        <InfoIconTooltip
          id="baselinePeriod"
          width={400}
          title={
            <Box>
              {intl.formatMessage({
                id: 'projects.cohortBuilderPage.studyPeriodDialogBaselinePeriod.infoLine1',
              })}
              <br />
              <br />
              {intl.formatMessage({
                id: 'projects.cohortBuilderPage.studyPeriodDialogBaselinePeriod.infoLine2',
              })}
            </Box>
          }
        />
      </Box>

      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          mt: theme.spacing(2),
        }}
      >
        <Tooltip
          id="baselinePeriodCheckboxTitle"
          arrow
          placement="top"
          title={
            hasIndexDate
              ? undefined
              : intl.formatMessage({
                  id: 'projects.cohortBuilderPage.studyPeriodDialogBaselinePeriodCheckbox.title.disabled',
                })
          }
        >
          <Checkbox
            sx={{
              marginLeft: `-${theme.spacing(3)}`,
              cursor: hasIndexDate ? 'pointer' : 'default',
              color: hasIndexDate ? undefined : theme.palette.text.disabled,
            }}
            aria-checked={isBaselinePeriodRestrictionEnabled}
            checked={isBaselinePeriodRestrictionEnabled}
            onClick={onClickBaselinePeriodRestrictionCheckbox}
            disableRipple={viewMode || !hasIndexDate}
            disableFocusRipple={viewMode || !hasIndexDate}
            disableTouchRipple={viewMode || !hasIndexDate}
          />
        </Tooltip>
        <Typography
          sx={{
            cursor: viewMode ? 'default' : 'pointer',
            color: hasIndexDate ? undefined : theme.palette.text.disabled,
          }}
          variant="body1"
          onClick={onClickBaselinePeriodRestrictionCheckbox}
        >
          {intl.formatMessage({
            id: 'projects.cohortBuilderPage.studyPeriodDialogBaselinePeriod.checkboxLabel',
          })}
        </Typography>
      </Box>

      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          mt: theme.spacing(2),
          mb: theme.spacing(6),
        }}
      >
        <TextField
          id=""
          displayLabel={intl.formatMessage({
            id: 'projects.cohortBuilderPage.studyPeriodDialogBaselinePeriod.baselineEditLabel',
          })}
          placeholder={intl.formatMessage({
            id: 'projects.cohortBuilderPage.studyPeriodDialogBaselinePeriod.baselineEditPlaceholder',
          })}
          type="integer"
          value={baselinePeriodDaysBeforeIndexDate?.toString() ?? ''}
          width={parseInt(theme.spacing(35), 10)}
          onChange={onDaysBeforeIndexDateChange}
          disabled={!isBaselinePeriodRestrictionEnabled || viewMode}
        />
        <Typography
          sx={{
            marginLeft: theme.spacing(4),
            color: isBaselinePeriodRestrictionEnabled
              ? theme.palette.text.primary
              : theme.palette.text.disabled,
          }}
          variant="body1"
        >
          {intl.formatMessage({
            id: 'projects.cohortBuilderPage.studyPeriodDialogBaselinePeriod.baselineEditMessage',
          })}
        </Typography>
      </Box>
    </Box>
  );
};
